import { ChevronDown, ExternalLink, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { drawResolvedTs, type ArchiveRound } from './api';
import { GAME_LABELS, gameNameKey } from '../../shared/game/betOptions';
import { utcDateTime, localDateTime } from '../../shared/lib/datetime';
import { diceFacePips } from '../../pages/archive/rulesExample';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { copyText } from '../../shared/lib/clipboard';
import { blockDigitRows, columnSums, drawDigits, roundCloseTimestamp } from '../verify/roundDigits';
import { RoundDigitsTable } from '../verify/RoundDigitsTable';
import { EtherscanHelpSheet, RoundAnchorCards, etherscanUrl } from '../verify/RoundAnchorCards';

export function DrawDetailContent({ round }: { round: ArchiveRound }) {
  const t = useI18nStore((state) => state.t);
  const [etherscanBlock, setEtherscanBlock] = useState<number | null>(null);
  const [copyFeedback, setCopyFeedback] = useState('');
  // Legacy showed the whole proof walkthrough without a fold — default open
  const [verifyExpanded, setVerifyExpanded] = useState(true);
  const games = round.games || {};
  const dice = games.sicbo || [];
  const diceSum = dice.reduce((sum, item) => sum + Number(item || 0), 0);
  const diceTriple = dice.length === 3 && dice.every((item) => Number(item) === Number(dice[0]));
  const rouletteValue = Number(games.roulette ?? -1);
  const rouletteTone = rouletteValue >= 0 ? rouletteColor(rouletteValue) : null;
  const rouletteTags = rouletteTone ? rouletteResultTags(rouletteValue, rouletteTone, t) : [];
  const digits = drawDigits(round);
  const blockDigits = blockDigitRows(round);
  const triples = digitTriples(digits);
  const closeTs = roundCloseTimestamp(round);
  const firstAnchorBlock = firstBlockNumber(round);
  const anchors = round.anchors || [];

  return (
    <>
      <section className="draw-proof-header-react" data-draw-proof-header-react="true">
        <div className="draw-proof-time-grid-react">
          <span>{t('roundDrawDay')} · UTC</span>
          <strong>{utcDateTime(drawResolvedTs(round))}</strong>
          <span>{t('roundDrawDay')} · {t('localTimeTag')}</span>
          <strong>{localDateTime(drawResolvedTs(round))}</strong>
        </div>
        <span className="draw-verified-chip-react">
          <ShieldCheck size={13} aria-hidden="true" />
          {t('verified')}
        </span>
      </section>

      <section className="draw-detail-results-react">
        <div>
          <span>{t(gameNameKey('lastdigit'))}</span>
          <span className="dd-token">
            <span className={`dd-lotto-ball dd-lotto-${games.lastdigit == null ? 'none' : Number(games.lastdigit) >= 5 ? 'big' : 'small'}`}>
              <span className="dd-lotto-inner">{games.lastdigit ?? '-'}</span>
            </span>
          </span>
          <em>{games.lastdigit == null ? '-' : Number(games.lastdigit) >= 5 ? t('big') : t('small')} · {games.lastdigit == null ? '-' : Number(games.lastdigit) % 2 === 0 ? t('even') : t('odd')}</em>
        </div>
        <div>
          <span>{t(gameNameKey('sicbo'))}</span>
          <span className="dd-token">
            {dice.length ? dice.map((value, index) => <ResultDie key={index} n={Number(value)} />) : '—'}
          </span>
          <em>{diceSum || '-'} · {diceTriple ? t('analyticsFairTriple') : diceSum ? (diceSum >= 11 ? t('big') : t('small')) : '-'}</em>
        </div>
        <div>
          <span>{t(gameNameKey('roulette'))}</span>
          <span className="dd-token">
            {rouletteValue >= 0 ? (
              <span className="dd-roulette">
                <span className={`dd-roulette-pocket dd-roulette-pocket-${roulettePocketColor(rouletteValue)}`}>{rouletteValue}</span>
              </span>
            ) : '—'}
          </span>
          <em>{rouletteTags.length ? rouletteTags.join(' · ') : '-'}</em>
        </div>
      </section>

      {/* The calculator path lives here, always visible: B rows → column sum →
          keep the last digit → D row. Legacy taught it in one table; hiding the
          sum step behind a fold left users with ingredients and answers only. */}
      <section className="draw-proof-card-react draw-digits-card-react">
        <span>{t('drawDetailDigits')}</span>
        <RoundDigitsTable blockDigits={blockDigits} digits={digits} />
        {/* 解释性 callout 原样保留在这里；门槛条件与 RoundDigitsTable 内部判断
            走不走真实表是同一条（都用共享的 columnSums），两边不会不同步。 */}
        {blockDigits.length > 0 && columnSums(blockDigits).length === 10 ? (
          <div className="rules-explain-callout">
            <span className="rules-explain-icon">💡</span>
            <div className="rules-diy-body-wrap">
              <div className="rules-explain-title">{t('rulesPart2ExplainTitle')}</div>
              <p className="rules-explain-body">{t('rulesPart2ExplainBody')}</p>
            </div>
          </div>
        ) : null}
        <p>{t('drawDetailFairness')}</p>
      </section>

      <section className="draw-proof-card-react draw-verify-card-react" data-draw-verify-react="true">
        <span>
          <ShieldCheck size={14} aria-hidden="true" />
          {t('drawVerifyTitle')}
        </span>
        <div className="draw-verify-step-react">
          <b>1</b>
          <p>{digits ? t('drawVerifyStepDigits').replace('%d%', digits) : t('drawVerifyStepMissing')}</p>
        </div>
        <div className="draw-verify-step-react">
          <b>2</b>
          <p>{lastDigitExplanation(digits, games.lastdigit, t)}</p>
        </div>
        <div className="draw-verify-step-react">
          <b>3</b>
          <p>{sicboExplanation(triples, dice, t)}</p>
        </div>
        <div className="draw-verify-step-react">
          <b>4</b>
          <p>{rouletteExplanation(triples, rouletteValue, t)}</p>
        </div>
      </section>

      <button
        className="draw-verify-toggle-react"
        type="button"
        aria-expanded={verifyExpanded}
        onClick={() => setVerifyExpanded((value) => !value)}
        data-draw-verify-toggle-react="true"
      >
        <span>{t(verifyExpanded ? 'verifyCollapse' : 'verifyExpand')}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>

      {verifyExpanded ? (
        <>
          <RoundAnchorCards anchors={anchors} roundCloseTs={closeTs} />

          <VerifyDerivationPanel
            digits={digits}
            blockDigits={blockDigits}
            triples={triples}
            lastdigit={games.lastdigit}
            dice={dice}
            rouletteValue={rouletteValue}
          />
        </>
      ) : null}

      {/* Per-block proof actions now live inside the Block 1/2/3 cards above —
          the separate proof module duplicated number/time/hash a second time. */}
      {anchors.length === 0 ? (
        <section className="draw-proof-card-react">
          <span>{t('drawDetailProof')}</span>
          <p>{t('drawDetailNoProof')}</p>
        </section>
      ) : null}

      {firstAnchorBlock ? (
        <button className="draw-verify-etherscan-cta-react" type="button" onClick={() => setEtherscanBlock(firstAnchorBlock)}>
          <ShieldCheck size={16} aria-hidden="true" />
          <span>{t('verifyEtherscan')}</span>
          <ExternalLink size={16} aria-hidden="true" />
        </button>
      ) : null}

      <p className="draw-verify-footer-react">{t('verifyFooter')}</p>

      {etherscanBlock ? (
        <EtherscanHelpSheet
          blockNumber={etherscanBlock}
          feedback={copyFeedback}
          onCopy={async () => {
            const ok = await copyText(etherscanUrl(etherscanBlock));
            setCopyFeedback(t(ok ? 'etherscanCopyOk' : 'etherscanCopyFail'));
            window.setTimeout(() => setCopyFeedback(''), 1800);
          }}
          onOpen={() => {
            window.open(etherscanUrl(etherscanBlock), '_blank', 'noopener,noreferrer');
            setEtherscanBlock(null);
          }}
          onClose={() => {
            setCopyFeedback('');
            setEtherscanBlock(null);
          }}
        />
      ) : null}
    </>
  );
}

function VerifyDerivationPanel({
  digits,
  blockDigits,
  triples,
  lastdigit,
  dice,
  rouletteValue,
}: {
  digits: string;
  blockDigits: string[];
  triples: number[];
  lastdigit: number | null | undefined;
  dice: number[];
  rouletteValue: number;
}) {
  const t = useI18nStore((state) => state.t);
  const columns = columnSums(blockDigits);
  const hasColumns = columns.length === 10;
  const hasTriples = triples.length >= 3;
  if (!digits && !hasColumns && !hasTriples) return null;
  const sicboRows = hasTriples ? triples.slice(0, 3).map((value, index) => ({
    label: `M${index + 1}`,
    value,
    quotient: Math.floor(value / 6),
    remainder: value % 6,
    result: dice[index] ?? ((value % 6) + 1),
  })) : [];
  // 轮盘除的是整个十位开奖数字，与后端 ops-node 的 draw.ts 同一条规则。
  // 不是三段 M 相加——那个式子漏掉 D₁，而且要读者先接受分组才看得懂。
  const rouletteDigits = digits && digits.length === 10 ? digits : '';
  const rouletteQuotient = rouletteDigits ? Number(BigInt(rouletteDigits) / 37n) : 0;
  const rouletteRemainder = rouletteDigits ? Number(BigInt(rouletteDigits) % 37n) : 0;

  return (
    <section className="draw-proof-card-react draw-derivation-card-react" data-draw-derivation-react="true">
      <span>{t('drawVerifyDerivation')}</span>

      {/* The B/Soma/D table now lives in the always-visible digits card —
          this panel only walks each game's division from the D values. */}
      <div className="draw-game-derivation-grid-react">
        <article>
          <span>{t(gameNameKey('lastdigit'))}</span>
          <div className="rules-game-d-display">
            <span className="rules-game-d-label">D₁ =</span>
            <span className="rules-result-box">{lastdigit ?? digits[0] ?? '-'}</span>
          </div>
        </article>
        {sicboRows.length ? (
          <article>
            <span>{t(gameNameKey('sicbo'))}</span>
            {sicboRows.map((row) => (
              <div className="rules-sicbo-step" key={row.label}>
                <div className="rules-sicbo-step-body">
                  <div className="rules-sicbo-modline">{row.label} = {row.value} · {row.value} ÷ 6 = {row.quotient}, {t('rulesPart2Sobra')} {row.remainder}</div>
                  <div className="rules-sicbo-resultline">{t('rulesGameSicboDieLabel')} {row.label.slice(1)}: {row.remainder} + 1 =</div>
                </div>
                <span className="rules-die-result">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="100%" height="100%">
                    <rect x="2" y="2" width="20" height="20" rx="3.5" fill="#fef3c7" stroke="#78350f" strokeWidth="0.6" />
                    {diceFacePips(row.result).map(([cx, cy], pipIndex) => <circle key={pipIndex} cx={cx} cy={cy} r="1.7" fill="#1c1917" />)}
                  </svg>
                </span>
              </div>
            ))}
          </article>
        ) : null}
        {rouletteDigits ? (
          <article>
            <span>{t(gameNameKey('roulette'))}</span>
            <div className="rules-roulette-line">{rouletteDigits} ÷ 37 = {rouletteQuotient}, {t('rulesPart2Sobra')} {rouletteRemainder}</div>
            <div className="rules-game-d-display rules-game-d-display-roulette">
              <span className={`rules-roulette-pocket rules-roulette-pocket-${roulettePocketColor(rouletteValue >= 0 ? rouletteValue : rouletteRemainder)}`}>{rouletteValue >= 0 ? rouletteValue : rouletteRemainder}</span>
            </div>
          </article>
        ) : null}
      </div>
    </section>
  );
}

function firstBlockNumber(round: ArchiveRound) {
  const firstAnchor = (round.anchors || []).find((anchor) => Number(anchor.number || 0) > 0);
  return firstAnchor ? Number(firstAnchor.number || 0) : 0;
}

function digitTriples(digits: string) {
  if (digits.length < 9) return [];
  // M1..M3 are D2-D4, D5-D7, D8-D10 — the grouping the draw engine uses
  // (backend/entry/ops-node/src/draw.ts slices 1-4 / 4-7 / 7-10). Slicing from
  // 0 published a derivation that never reproduced the posted result, which is
  // exactly what this section invites players to check.
  return [digits.slice(1, 4), digits.slice(4, 7), digits.slice(7, 10)].map((value) => Number(value));
}

function lastDigitExplanation(digits: string, result: number | null | undefined, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (!digits) return t('drawVerifyStepMissing');
  return t('drawVerifyLastdigit').replace('%d1%', digits[0] || '-').replace('%result%', String(result ?? digits[0] ?? '-'));
}

function sicboExplanation(triples: number[], dice: number[], t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (triples.length < 3 || dice.length < 3) return t('drawVerifyStepMissing');
  const parts = triples.map((value, index) => `${value} mod 6 + 1 = ${dice[index]}`);
  return t('drawVerifySicbo').replace('%steps%', parts.join(' · '));
}

function rouletteExplanation(triples: number[], result: number, t: ReturnType<typeof useI18nStore.getState>['t']) {
  if (triples.length < 3 || result < 0) return t('drawVerifyStepMissing');
  return t('drawVerifyRoulette')
    .replace('%sum%', triples.join(' + '))
    .replace('%result%', String(result));
}

function rouletteColor(value: number): 'red' | 'black' | 'green' {
  if (value === 0) return 'green';
  return new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]).has(value) ? 'red' : 'black';
}

function rouletteResultTags(value: number, tone: 'red' | 'black' | 'green', t: ReturnType<typeof useI18nStore.getState>['t']) {
  const tags = [tone === 'red' ? t('red') : tone === 'black' ? t('black') : t('green')];
  if (value === 0) return tags;
  tags.push(value % 2 === 0 ? t('even') : t('odd'));
  tags.push(value >= 19 ? '19–36' : '1–18');
  tags.push(t(value <= 12 ? 'roulDozen_dz1' : value <= 24 ? 'roulDozen_dz2' : 'roulDozen_dz3'));
  return tags;
}

function shortHash(hash: string) {
  if (!hash) return '-';
  return hash.length > 18 ? `${hash.slice(0, 10)}...${hash.slice(-6)}` : hash;
}

function ResultDie({ n }: { n: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
      <rect x="2" y="2" width="20" height="20" rx="4" fill="#fef3c7" stroke="#78350f" strokeWidth="0.7" />
      {diceFacePips(n).map(([cx, cy], pipIndex) => (
        <circle key={pipIndex} cx={cx} cy={cy} r="1.9" fill="#1c1917" />
      ))}
    </svg>
  );
}

const ROULETTE_REDS = new Set([1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36]);
function roulettePocketColor(n: number): 'red' | 'black' | 'green' {
  if (n === 0) return 'green';
  return ROULETTE_REDS.has(n) ? 'red' : 'black';
}
